import type { Messages } from '@/types'

const messages: Messages = {
    common: {
        actions: {
            add: 'Ajouter',
            edit: 'Modifier',
            delete: 'Supprimer',
            cancel: 'Annuler',
            back: 'Retour',
            save: 'Enregistrer',
            update: 'Mettre à jour',
            reset: 'Réinitialiser',
            refresh: 'Actualiser',
            backToList: '@:common.back à la liste',
            resetDefaults: '@:common.reset aux valeurs par défaut',
            confirm: 'Confirmer',
            proceed: 'Continuer',
            close: 'Fermer',
            apply: 'Appliquer',
            search: 'Rechercher',
            filter: 'Filtrer',
            clear: 'Effacer',
            select: 'Sélectionner',
            upload: 'Télécharger',
            download: 'Télécharger',
            view: 'Voir',
            sort: 'Trier',
            seeMore: 'Voir plus'
        },
        status: {
            success: 'Succès',
            error: 'Erreur',
            loading: 'Chargement...'
        },
        messages: {
            deleteConfirm: 'Êtes-vous sûr de vouloir supprimer cet utilisateur?',
            generalError: 'Quelque chose s\'est mal passé! Veuillez réessayer',
            addSuccess: 'Enregistrement ajouté avec succès!',
            updateSuccess: 'Enregistrement mis à jour avec succès!',
        },
        placeholder: {
            avatar: '?'
        },
        labels: {
            loading: 'Chargement',
            noResults: 'Aucun résultat trouvé',
            noData: 'Aucune donnée disponible',
            selected: 'Sélectionné',
            required: 'Requis',
            optional: 'Optionnel',
            search: 'Rechercher...',
            filter: 'Filtrer...',
            from: 'De',
            to: 'À',
            date: 'Date',
            status: 'Statut',
            item: 'Élément {index}',
            addItem: 'Ajouter un Élément',
            removeItem: 'Supprimer l\'Élément',
            skipToMain: 'Aller au contenu principal',
            openNavigation: 'Ouvrir la navigation',
            closeNavigation: 'Fermer la navigation',
            mainNavigation: 'Navigation principale',
            moveUp: 'Déplacer vers le Haut',
            moveDown: 'Déplacer vers le Bas'
        },
        errors: {
            upload: 'Échec du téléchargement du fichier',
            download: 'Échec du téléchargement du fichier',
            generic: 'Une erreur s\'est produite',
            notFound: 'Non trouvé',
            unauthorized: 'Accès non autorisé',
            validation: 'Veuillez vérifier votre saisie'
        },
        validation: {
            required: 'Ce champ est obligatoire',
            email: 'Adresse e-mail non valide',
            minLength: 'La longueur minimale est {length}',
            maxLength: 'La longueur maximale est {length}',
            min: 'La valeur minimale est {min}',
            max: 'La valeur maximale est {max}',
            matches: 'Format non valide',
            unique: 'La valeur doit être unique',
            confirm: 'Les mots de passe ne correspondent pas'
        }
    },
    pages: {
        users: {
            title: 'Utilisateurs',
            description: 'Gérer les utilisateurs du système',
            actions: {
                invite: 'Inviter un Utilisateur',
                resetPassword: 'Réinitialiser le Mot de Passe'
            }
        },
        settings: {
            title: 'Paramètres',
            actions: {
                resetDefaults: 'Réinitialiser aux valeurs par défaut'
            }
        },
        reports: {
            title: 'Rapports',
            description: 'Générer et télécharger des rapports',
            actions: {
                generateReport: 'Générer un Rapport',
                downloadCsv: 'Télécharger CSV',
                startDate: 'Date de Début',
                endDate: 'Date de Fin',
                apps: 'Applications',
            },
            error: {
                generation: 'Échec de la génération du rapport',
                download: 'Échec du téléchargement du rapport'
            }
        }
    },
    navigation: {
        home: 'Accueil',
        library: 'Bibliothèque',
        search: 'Rechercher',
        login: 'Connexion',
        signInToGetPremium: 'Obtenir Premium',
        subscribe: 'S\'abonner',
        support: 'Support',
        settings: 'Paramètres',
        logout: 'Déconnexion'
    },
    settings: {
        title: 'Paramètres',
        description: 'Gérez les paramètres de votre compte, vos préférences et les informations de votre profil.',
        sections: 'Sections des Paramètres',
        menu: {
            profile: 'Profil',
            account: 'Compte',
            general: 'Général',
            purchases: 'Achats',
            activity: 'Activité'
        },
        profile: {
            title: 'Profil',
            changeImage: 'Changer l\'Image',
            updateProfile: 'Mettre à Jour le Profil',
            changePassword: 'Changer le Mot de Passe',
            updatePassword: 'Mettre à Jour le Mot de Passe',
            fields: {
                fullName: 'Nom Complet',
                emailAddress: 'Adresse Email',
                dateOfBirth: 'Date de Naissance',
                currentPassword: 'Mot de Passe Actuel',
                newPassword: 'Nouveau Mot de Passe',
                confirmNewPassword: 'Confirmer le Nouveau Mot de Passe'
            },
            toasts: {
                imageUpdated: 'Image Mise à Jour',
                imageUpdatedMessage: 'Votre image de profil a été mise à jour avec succès',
                uploadFailed: 'Échec du Téléchargement',
                profileUpdated: 'Profil Mis à Jour',
                profileUpdatedMessage: 'Votre profil a été mis à jour avec succès',
                passwordUpdated: 'Mot de Passe Mis à Jour',
                passwordUpdatedMessage: 'Votre mot de passe a été mis à jour avec succès',
                passwordError: 'Erreur de Mot de Passe',
                passwordMismatch: 'Les mots de passe ne correspondent pas',
                passwordTooWeak: 'Le mot de passe est trop faible'
            }
        },
        account: {
            title: 'Compte',
            accountInformation: 'Informations du Compte',
            emailAddress: 'Adresse Email',
            memberSince: 'Membre depuis',
            redeemPromoCode: 'Utiliser un code promo',
            redeemPromoCodeDescription: 'Vous avez un code promo ou cadeau ? Saisissez-le ici pour l\'appliquer à votre compte ou à votre prochain abonnement.',
            subscription: {
                title: 'Abonnement',
                currentPlan: 'Plan Actuel',
                premiumSubscription: 'Abonnement Premium',
                nextRenewal: 'Prochain renouvellement:',
                accessUntil: 'Accès jusqu\'au:',
                manageInAppStore: 'Votre abonnement a été acheté via l\'App Store. Pour l\'annuler ou le modifier, ouvrez Abonnements dans les réglages de votre identifiant Apple sur l\'appareil utilisé pour vous abonner.',
                manageInPlayStore: 'Votre abonnement a été acheté via Google Play. Pour l\'annuler ou le modifier, ouvrez Abonnements dans le Google Play Store sur l\'appareil utilisé pour vous abonner.',
                notSubscribed: 'Vous n\'êtes actuellement abonné à aucun plan.',
                subscribeNow: 'S\'abonner Maintenant',
                findOutMore: 'En savoir plus',
                cancelSubscription: 'Annuler l\'Abonnement',
                cancelSubscriptionTitle: 'Annuler l\'Abonnement',
                cancelSubscriptionMessage: 'Êtes-vous sûr de vouloir annuler votre abonnement? Vous perdrez l\'accès au contenu premium à la fin de votre période de facturation actuelle.',
                keepSubscription: 'Conserver l\'Abonnement',
                toasts: {
                    subscriptionActivated: 'Abonnement Activé',
                    subscriptionActivatedMessage: 'Bienvenue sur Premium!',
                    subscriptionFailed: 'Échec de l\'Abonnement',
                    paymentMethodUpdated: 'Méthode de Paiement Mise à Jour',
                    paymentMethodUpdatedMessage: 'Vos informations de paiement ont été mises à jour avec succès',
                    subscriptionCancelled: 'Abonnement Annulé',
                    subscriptionCancelledMessage: 'Votre abonnement se terminera à la période de facturation actuelle',
                    cancellationFailed: 'Échec de l\'Annulation'
                }
            },
            closeAccount: {
                title: 'Fermer le Compte',
                requestDescription: 'Demander la fermeture permanente de votre compte. Cette action ne peut pas être annulée.',
                requestButton: 'Demander la Fermeture du Compte',
                confirmationTitle: 'Êtes-vous sûr de vouloir désactiver votre compte? Cela va:',
                confirmationItems: {
                    cancelSubscription: 'Annuler votre abonnement actif (le cas échéant)',
                    removeCommunications: 'Vous retirer de toutes les communications',
                    scheduleDeletion: 'Programmer la suppression permanente de vos données après 30 jours'
                },
                confirmationWarning: 'Vous pouvez annuler votre demande de désactivation à tout moment dans les 30 jours. Après cette période, votre compte et toutes les données associées seront supprimés de façon permanente.',
                confirmationCheckbox: 'Je comprends que mon compte sera supprimé de façon permanente après 30 jours',
                deactivateAccount: 'Désactiver le Compte',
                cancel: 'Annuler',
                scheduledTitle: 'Désactivation du Compte Programmée',
                scheduledMessage: 'Votre compte est programmé pour être supprimé de façon permanente le',
                scheduledWarning: 'Pendant cette période, vous ne pourrez pas vous connecter à votre compte. Si vous changez d\'avis, vous pouvez annuler le processus de désactivation.',
                cancelDeactivation: 'Annuler la Désactivation du Compte',
                toasts: {
                    deactivationRequested: 'Désactivation du Compte Demandée',
                    deactivationRequestedMessage: 'Votre compte sera supprimé de façon permanente dans 30 jours',
                    deactivationFailed: 'Échec de la Désactivation',
                    deactivationCancelled: 'Désactivation Annulée',
                    deactivationCancelledMessage: 'Votre compte n\'est plus programmé pour suppression'
                }
            }
        },
        purchases: {
            title: 'Historique des Achats',
            noPurchases: 'Aucun achat trouvé',
            invoice: 'Facture'
        },
        activity: {
            title: 'Chronologie des Activités',
            dateRange: 'Plage de Dates',
            activityType: 'Type d\'Activité',
            noActivities: 'Aucune activité trouvée',
            resume: 'Reprendre',
            loadMore: 'Charger Plus',
            filters: {
                last7Days: '7 derniers jours',
                last30Days: '30 derniers jours',
                lastYear: 'Dernière année',
                allTime: 'Tout le temps',
                allActivities: 'Toutes les Activités',
                listening: 'Écoute',
                purchases: 'Achats',
                likes: 'J\'aime',
                bookmarks: 'Signets'
            },
            types: {
                listenedTo: 'Écouté {title}',
                purchased: 'Acheté {title}',
                liked: 'Aimé {title}',
                bookmarked: 'Marqué {title}'
            }
        },
        appSettings: {
            title: 'Paramètres de l\'Application',
            darkMode: 'Mode Sombre',
            language: 'Langue',
            languageOptions: {
                english: 'Anglais',
                spanish: 'Espagnol',
                french: 'Français',
                german: 'Allemand',
                italian: 'Italien',
                portuguese: 'Portugais',
                dutch: 'Néerlandais',
                slovak: 'Slovaque',
                russian: 'Russe',
                arabic: 'العربية'
            }
        },
        notifications: {
            title: 'Notifications',
            emailNotifications: 'Notifications par Email',
            emailNotificationsHint: 'Recevoir des mises à jour par email',
            pushNotifications: 'Notifications Push',
            pushNotificationsHint: 'Recevoir des notifications push',
            updatePreferences: 'Mettre à Jour les Préférences'
        },
        toasts: {
            themeUpdated: 'Thème Mis à Jour',
            themeUpdatedMessage: 'Votre préférence de thème a été enregistrée',
            languageUpdated: 'Langue Mis à Jour',
            languageUpdatedMessage: 'Votre préférence de langue a été enregistrée',
            preferencesUpdated: 'Préférences Mises à Jour',
            preferencesUpdatedMessage: 'Vos préférences de notification ont été enregistrées',
            updateFailed: 'Échec de la Mise à Jour',
            updateFailedMessage: 'Échec de la mise à jour du paramètre',
            updateFailedNotificationMessage: 'Échec de la mise à jour des préférences de notification'
        }
    },
    auth: {
        login: {
            title: 'Se Connecter',
            description: 'Connectez-vous à votre compte Heard pour accéder à votre bibliothèque et au contenu premium',
            signIn: 'Se Connecter',
            signInToAccount: 'Connectez-vous à votre compte',
            noAccount: 'Vous n\'avez pas de compte?',
            signUp: 'S\'inscrire',
            continueAsGuest: 'Continuer en tant qu\'invité',
            or: 'Ou',
            agreeToTerms: 'En vous connectant, vous acceptez nos',
            termsOfService: 'Conditions d\'Utilisation',
            privacyPolicy: 'Politique de Confidentialité',
            forgotPassword: 'Mot de Passe Oublié?'
        },
        register: {
            title: 'S\'inscrire',
            description: 'Créez un nouveau compte Heard pour accéder à votre bibliothèque et au contenu premium',
            createAccount: 'Créer un Compte',
            createYourAccount: 'Créez votre compte',
            orSignIn: 'O',
            signIn: 'se connecter',
            backToHome: 'Retour à la page d\'accueil',
            agreeToTerms: 'En créant un compte, vous acceptez nos',
            termsOfService: 'Conditions d\'Utilisation',
            privacyPolicy: 'Politique de Confidentialité'
        },
        forms: {
            email: 'Adresse e-mail',
            password: 'Mot de Passe',
            confirmPassword: 'Confirmer le Mot de Passe',
            fullName: 'Nom Complet',
            rememberMe: 'Se souvenir de moi',
            signIn: 'Se Connecter',
            createAccount: 'Créer un Compte'
        }
    },
    player: {
        chapters: 'Chapitres',
        options: 'Options',
        playbackSpeed: 'Vitesse de Lecture',
        share: 'Partager',
        linkCopied: 'Lien Copié',
        linkCopiedMessage: 'Lien de partage copié dans le presse-papiers!',
        shareError: 'Erreur de Partage',
        shareErrorMessage: 'Impossible de copier le lien. Veuillez réessayer.',
        bookmarkAdded: 'Succès',
        bookmarkAddedMessage: 'Marque-page ajouté!',
        jumpedToBookmark: 'Succès',
        jumpedToBookmarkMessage: 'Saut au marque-page!',
        likeError: 'Erreur de J\'aime',
        likeErrorMessage: 'Impossible de mettre à jour le statut de j\'aime. Veuillez réessayer.',
        bookmarkError: 'Erreur de Marque-page',
        bookmarkErrorMessage: 'Impossible de mettre à jour le marque-page. Veuillez réessayer.',
        favorites: {
            add: 'Ajouter aux Favoris',
            remove: 'Retirer des Favoris'
        },
        unknownChapter: 'Chapitre Inconnu',
        unknownTitle: 'Titre Inconnu',
        unknownAuthor: 'Auteur Inconnu'
    },
    details: {
        starring: 'Avec:',
        author: 'Auteur',
        narrator: 'Narrateur',
        pause: 'Pause',
        purchased: 'Acheté',
        continueReading: 'Continuer la Lecture',
        continuePlaying: 'Continuer la Lecture',
        play: 'Lire',
        loading: 'Chargement...',
        noChapters: 'Aucun chapitre disponible',
        chapterFreeLabel: 'Gratuit',
        listen: 'Écouter',
        purchaseSuccess: 'Achat réussi',
        purchaseSuccessMessage: '{title} a été ajouté à votre bibliothèque',
        subscriptionSuccess: 'Abonnement réussi',
        subscriptionSuccessMessage: 'Bienvenue sur Heard Premium !',
        subscriptionFailed: 'Échec de l\'abonnement',
        subscriptionErrorMessage: 'Une erreur s\'est produite lors du traitement de votre abonnement.',
        info: 'Infos',
        more: 'Plus',
        episodes: 'Épisodes',
        about: 'À propos',
        series: 'Série',
        producedBy: 'Produit par',
        recommendations: 'Vous pourriez aussi aimer',
        moreFromCategory: 'Plus de {name}',
        moreFromAuthor: 'Plus de {name}',
        moreFromCast: 'Plus de {name}',
        moreFromSeries: 'Plus dans {name}',
        noRelated: 'Aucun titre associé disponible pour le moment.',
        ageVerification: {
            title: 'Réservé aux adultes',
            message: 'Ce contenu est réservé aux adultes. Veuillez confirmer que vous souhaitez continuer.',
            minAgeHint: 'Vous devez avoir au moins {age} ans pour voir ce titre.',
            confirm: 'Je suis adulte, continuer',
            cancel: 'Annuler',
            missingDobTitle: 'Date de naissance requise',
            missingDobMessage: 'Ajoutez votre date de naissance dans votre profil pour voir ce contenu.',
            underageTitle: 'Restriction d’âge',
            underageMessage: 'Vous devez avoir au moins {age} ans pour voir ce contenu.',
        },
    },
    shareModal: {
        title: 'Partager',
        copyLink: 'Copier le lien',
        copyLinkWithMessage: 'Copier le lien avec le message',
        copyLinkWithMessageHint: 'Titre et description inclus',
        shareVia: 'Partager via...',
        shareViaHint: 'Applications, messages, e-mail',
        linkCopied: 'Lien copié',
        linkCopiedMessage: 'Lien copié dans le presse-papiers !',
        shared: 'Partagé',
        sharedMessage: 'Contenu partagé avec succès'
    },
    contextMenu: {
        playFromBeginning: 'Lire depuis le Début',
        markAsFinished: 'Marquer comme Terminé',
        addToFavorites: 'Ajouter aux Favoris',
        removeFromFavorites: 'Retirer des Favoris',
        share: 'Partager',
        viewDetails: 'Voir les Détails',
        removeFromLibrary: 'Retirer de la Bibliothèque',
        continuePlaying: 'Continuer la Lecture',
        continueReading: 'Continuer la Lecture'
    },
    actions: {
        subscribe: 'S\'abonner',
        signInToGetPremium: 'Obtenir Premium',
        purchase: 'Acheter',
        buy: 'Acheter',
        buyFor: 'Acheter pour {price}',
        purchasedGoToLibrary: 'Acheté - Aller à la Bibliothèque',
        addToFavorites: 'Ajouter aux favoris',
        removeFromFavorites: 'Retirer des favoris',
        share: 'Partager'
    },
    browse: {
        sortBy: 'Trier par',
        sortOptions: {
            recent: 'Récent',
            ascending: 'A-Z',
            descending: 'Z-A'
        }
    },
    cookieConsent: {
        title: 'Cookies',
        message: 'Nous utilisons des cookies pour améliorer votre expérience de navigation et analyser notre trafic. En cliquant sur "Accepter", vous consentez à notre utilisation des cookies. En savoir plus dans notre',
        privacyPolicy: 'Politique de Confidentialité',
        accept: 'Accepter',
        reject: 'Refuser'
    },

    legal: {
        companyNumber: 'N° d\'entreprise',
        privacyPolicy: 'Politique de confidentialité',
        termsOfService: 'Conditions d\'utilisation',
        agreeToTermsSubscribe: 'En vous abonnant, vous acceptez nos',
        operatedBy: '{appName} est exploité par {companyName}, {address}',
        faqs: 'FAQ'
    },
    support: {
        title: 'Support',
        description: 'Besoin d\'aide avec votre compte, abonnement ou expérience d\'écoute ? Consultez nos FAQ et informations légales, ou envoyez-nous un message.',
        intro: 'Besoin d\'aide avec votre compte, abonnement ou expérience d\'écoute ? Consultez nos FAQ et informations légales ci-dessous, ou contactez-nous si vous avez besoin d\'aide supplémentaire.',
        quickLinks: 'Liens rapides',
        contactTitle: 'Contacter le support',
        contactDescription: 'Envoyez un message à notre équipe de support. Consultez d\'abord la FAQ pour des réponses rapides.',
        contactIntro: 'Si vous avez besoin d\'aide, nous sommes là pour vous. De nombreuses questions courantes sont déjà répondues dans notre centre d\'aide.',
        contactIntroFaqBefore: 'Consultez nos ',
        contactIntroFaqAfter: ' pour des réponses aux questions courantes. Si vous avez encore besoin d\'aide, remplissez le formulaire et nous vous répondrons dès que possible.',
        contactSubmit: 'Envoyer le message',
        contactName: 'Nom',
        contactEmail: 'E-mail',
        contactSubject: 'Objet',
        contactMessage: 'Message',
        contactMessageHint: 'Veuillez décrire votre problème en détail',
        backToSupport: 'Retour au support',
        pageLoading: 'Chargement…',
        pageNotAvailable: 'Cette page n\'est pas disponible pour le moment.',
        unsupportedSection: 'Section de contenu non prise en charge : {name}',
        useContactFormBefore: 'Besoin de nous contacter ? Utilisez notre ',
        contactSuccessTitle: 'Message envoyé',
        contactSuccessMessage: 'Votre message a été envoyé. Nous vous répondrons dès que possible.',
        contactErrorTitle: 'Échec de l\'envoi',
        contactErrorMessage: 'Impossible d\'envoyer votre message. Veuillez réessayer.'
    },
    promo: {
        haveCode: 'Vous avez un code promo ?',
        placeholder: 'Saisir le code',
        apply: 'Appliquer',
        remove: 'Retirer',
        applied: '{name} – {discount}',
        invalidCode: 'Code invalide ou expiré.',
        expired: 'Ce code a expiré.',
        notValidForPurchase: 'Ce code n\'est pas valable pour cet achat.',
        free: 'Gratuit',
        percentOff: '{value} % de réduction',
        fixedOff: '{value} de réduction',
        useWhenSubscribe: 'Utilisez cette offre lors de votre abonnement ci-dessous.',
        useWhenPurchase: 'Utilisez ce code lors de l\'achat d\'un titre.',
        accessGranted: 'L\'accès a été appliqué à votre compte.'
    }
}

export default messages

